import { Modal } from '../../../../components/Modal/Modal'
import React, { useEffect, useState } from 'react'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import {
  useGetIntegrationUsersQuery,
  useLazyGetIntegrationUsersQuery,
  useSaveResultMutation,
  useSaveResultToCRMMutation,
} from '../../../../store/integrations/integrations'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { FormProvider, useForm } from 'react-hook-form'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import 'react-datepicker/dist/react-datepicker.css'
import { addToast } from '../../../../store/toast/toastSlice'
import { CRMModalProps } from '../../../../apps/professional-app/fa/MeetingSummary/CRM/models'
import { selectRoomSession } from '../../../../store/meetingSummary/roomSession'
import './AddToCRMModal.scss'
import { getContactOptions } from '../utils'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../models/Account'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { AIAgentResult } from '../../../../models/AIAgent'
import { AiResult } from '../../../../models/AiResult'
import { useExportConfirmation } from '../../../../components/ConfirmationDialog/useExportConfirmation'
import { setAiResults } from '../../../../store/meetingSummary/aiResults'
import { useLazyListAiResultsQuery } from '../../../../store/media/media'
import { useSyncStatuses } from '../../../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/hooks'
import { IntegrationSavedModalBody } from '../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { PresetIds, TOPICS_COVERED_PRESET_IDS } from '../PresetMappings'
import { env } from '../../../../common/util'
import { IntegrationServiceId } from '../../../../common/integrations'
import { ContactDropdown } from '../ContactDropdown'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]
const MIGRATED_AI_FEATURE_PRESET_IDS: PresetIds[] = [TOPICS_COVERED_PRESET_IDS]

export type CRMFormValues = {
  externalUserId: string
}

export interface CRMSaveResultModalProps {
  handleSaveResult?: (data: CRMFormValues) => void
  isLoading?: boolean
  isSuccess?: boolean
}

export const AddResultDefaultToCRMModal: React.FC<CRMSaveResultModalProps & CRMModalProps<AiResult | AIAgentResult>> = ({
  show,
  onHide,
  aiFeature,
  result,
  serviceId,
  handleSaveResult,
  isLoading,
}) => {
  const _useAppDispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useExportConfirmation({ roomSession })

  const { tracked } = useTracking()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const [triggerSaveResults, { isLoading: isLoadingDefault }] = useSaveResultMutation()
  const [triggerSaveResultsToCRM, { isLoading: isLoadingToCRM }] = useSaveResultToCRMMutation()
  const [triggerGetAiResults, { isLoading: isGetAiResultsLoading }] = useLazyListAiResultsQuery()
  const [savedMeetingResult, setSavedMeetingResult] = useState<boolean>(false)
  const [syncTime, setSyncTime] = useState<Date | null>(null)
  const { aiResultsSyncStatus } = useSyncStatuses()

  const defaultValues = {
    externalUserId: '',
  }

  const _defaultHandleSave = async (data: CRMFormValues) => {
    if (!roomSession) {
      return
    }
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
    const isMigratedPreset = MIGRATED_AI_FEATURE_PRESET_IDS.some((presetIds) => presetIds[env] === aiFeature.preset?.id)

    const contactMapping =
      typeof serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
        ? null
        : (serviceIntegration?.mappings[AccountIntegrationConfigMappingType.CONTACT]?.objectName ?? 'contact')
    await runMutationWithConfirmation(async () => {
      setSyncTime(new Date())
      const response = isMigratedPreset
        ? await triggerSaveResultsToCRM({
            accountId,
            connector: serviceId,
            body: {
              relatedTo: { id: data.externalUserId },
              type: 'aiResult',
              resultId: result.id,
              result: result,
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
            },
          })
        : await triggerSaveResults({
            accountId,
            connector: serviceId,
            userId: currentUserId,
            body: {
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
              aiFeatureId: aiFeature.id,
              aiResultId: result.id,
              externalUserIds: [data.externalUserId],
              extra: {
                ...(contactMapping && { contact_type: contactMapping }),
              },
            },
          })

      if (!response.error) {
        const { data: aiResults } = await triggerGetAiResults({
          accountId,
          roomId: roomSession.roomId,
          sessionId: roomSession.id,
          pageRequest: { page: 0, size: 500 },
        }).unwrap()
        if (!aiResults) {
          return
        }
        _useAppDispatch(setAiResults(aiResults))
        setSavedMeetingResult(true)
      }
    })
  }

  const handleSave = tracked(
    (data: CRMFormValues) => {
      if (handleSaveResult) {
        handleSaveResult(data)

        /* THIS IS A TEMPORARY FIX BELOW */
        onHide()
        form.reset()
        _useAppDispatch(
          addToast({
            variant: 'success',
            title: 'Results saved',
            messages: ['Results were successfully saved to your CRM'],
          }),
        )
        /* THIS IS A TEMPORARY FIX ABOVE */
      } else {
        _defaultHandleSave(data)
      }
    },
    { eventName: TrackEvent.SAVE_RESULTS_TO_CRM },
    {
      eventDetailsExtractor: (data: CRMFormValues) => {
        return {
          ...(roomSession && { roomId: roomSession.roomId }),
          ...(roomSession && { sessionId: roomSession.id }),
          aiResultId: result.id,
          preset: aiFeature.preset?.id,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<CRMFormValues>({ defaultValues })
  const { handleSubmit } = form

  const handleOnHide = () => {
    onHide()
    form.reset()
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size={savedMeetingResult ? 'xl' : undefined}
        header={
          !savedMeetingResult ? (
            <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
              Save your Results to&nbsp;<span className='text-capitalize'>{serviceId}</span>
            </BootstrapModal.Title>
          ) : null
        }
        body={
          savedMeetingResult ? (
            <IntegrationSavedModalBody
              syncStatuses={aiResultsSyncStatus.length ? [aiResultsSyncStatus[0]] : []}
              serviceId={serviceId as IntegrationServiceId}
              syncTime={syncTime}
              onHide={handleOnHide}
              fullWidth
            />
          ) : (
            <FormProvider {...form}>
              <ContactsDropdown serviceId={serviceId} />
            </FormProvider>
          )
        }
        showFooterActions={!savedMeetingResult}
        onFooterPrimaryAction={handleSubmit(handleSave)}
        footerPrimaryActionLoading={isLoading ? isLoading : isLoadingDefault || isLoadingToCRM || accountIsLoading || isGetAiResultsLoading}
        onFooterSecondaryAction={onHide}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

const ContactsDropdown: React.FC<{
  serviceId: IntegrationServiceId
}> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })

  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    if (contacts && contacts.data) {
      setContactOptions(getContactOptions(contacts.data, serviceId))
    }
  }, [contacts, serviceId])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({
        accountId,
        connector: serviceId,
        filters: { name: searchInput },
      }).unwrap()
      if (contactsData && contactsData.data) {
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (isContactsFetching && !contacts) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }

  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactDropdown fieldName='externalUserId' contactOptions={contactOptions} serviceId={serviceId} onSearchInputChange={onSearchInputChange} />
        </Col>
      </Row>
    </Container>
  )
}

export interface AddResultCustomToCRMModalProps {
  contactId: string
}

export const AddResultCustomToCRMModal: React.FC<CRMSaveResultModalProps & CRMModalProps<AiResult | AIAgentResult> & AddResultCustomToCRMModalProps> = ({
  show,
  onHide,
  result,
  serviceId,
  handleSaveResult,
  isLoading,
  isSuccess,
  contactId,
}) => {
  const _useAppDispatch = useAppDispatch()
  const roomSession = useAppSelector(selectRoomSession)

  const { tracked } = useTracking()

  const defaultValues = {
    externalUserId: '',
  }

  const handleSave = tracked(
    (data: CRMFormValues) => {
      if (handleSaveResult) {
        handleSaveResult(data)
      }
    },
    { eventName: TrackEvent.SAVE_RESULTS_TO_CRM },
    {
      eventDetailsExtractor: () => {
        return {
          ...(roomSession && { roomId: roomSession.roomId }),
          ...(roomSession && { sessionId: roomSession.id }),
          aiResultId: result.id,
          externalUserId: contactId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<CRMFormValues>({ defaultValues })
  const { handleSubmit } = form

  useEffect(() => {
    if (isSuccess) {
      onHide()
      form.reset()
      _useAppDispatch(
        addToast({
          variant: 'success',
          title: 'Results saved',
          messages: ['Results were successfully saved to your system'],
        }),
      )
    }
  }, [form, onHide, _useAppDispatch, isSuccess])

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>Save your Results to your system.</BootstrapModal.Title>}
      body={
        <FormProvider {...form}>
          <div>Are you sure you want to save this to your system?</div>
        </FormProvider>
      }
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isLoading}
      onFooterSecondaryAction={onHide}
      centered={true}
    />
  )
}
